import React, { forwardRef, useCallback, useEffect } from 'react'
import { useActive, useChainedCommands, useCommands } from '@remirror/react'

import {
  Bold as BoldIcon,
  Heading as HeadingIcon,
  Image as ImageIcon,
  Italic as ItalicIcon,
  List as ListIcon,
  ListOrdered as OrderedListIcon,
  Minus as HorizontalRuleIcon,
  TextQuote as TextQuoteIcon,
  TvMinimalPlay as EmbedIcon,
} from 'lucide-react'

import Button from './Button'
import Uploader from './Uploader'

const BlockquoteButton = (props) => {
  const { toggleBlockquote } = useCommands()

  const active = useActive().blockquote()
  const enabled = toggleBlockquote.enabled()
  const onClick = useCallback(() => toggleBlockquote(), [toggleBlockquote])

  return (
    <Button active={active} enabled={enabled} onClick={onClick} {...props}>
      <TextQuoteIcon size={16} />
    </Button>
  )
}

const HeadingButton = (props) => {
  const { toggleHeading } = useCommands()
  const options = { level: 2 };

  const active = useActive().heading(options)
  const enabled = toggleHeading.enabled(options)
  const onClick = useCallback(() => toggleHeading(options), [toggleHeading])

  return (
    <Button active={active} enabled={enabled} onClick={onClick} {...props}>
      <HeadingIcon size={16} />
    </Button>
  )
}

const BoldButton = (props) => {
  const { toggleBold } = useCommands()

  const active = useActive().bold()
  const enabled = toggleBold.enabled()
  const onClick = useCallback(() => toggleBold(), [toggleBold])

  return (
    <Button active={active} enabled={enabled} onClick={onClick} {...props}>
      <BoldIcon size={16} />
    </Button>
  )
}

const ItalicButton = (props) => {
  const { toggleItalic } = useCommands()

  const active = useActive().italic()
  const enabled = toggleItalic.enabled()
  const onClick = useCallback(() => toggleItalic(), [toggleItalic])

  return (
    <Button active={active} enabled={enabled} onClick={onClick} {...props}>
      <ItalicIcon size={16} />
    </Button>
  )
}

const HorizontalRuleButton = (props) => {
  const { insertHorizontalRule } = useCommands()

  const enabled = insertHorizontalRule.enabled()
  const onClick = useCallback(() => {
    insertHorizontalRule()
  }, [insertHorizontalRule]);

  return (
    <Button enabled={enabled} onClick={onClick} {...props}>
      <HorizontalRuleIcon size={16} />
    </Button>
  )
}

const ImageButton = ({
  fileInput,
  previewPath,
  progressContainer,
}, ...props) => {
  const { insertImage } = useCommands()

  const enabled = insertImage.enabled()

  useEffect(() => {
    const onChange = (event) => {
      Array.from(event.target.files).map((file) => {
        const uploader = new Uploader(file, progressContainer, fileInput)

        uploader.upload(({ filename: fileName, signed_id: signedID }) => {
          insertImage({
            fileName,
            signedID,
            src:
              previewPath
                .replace(':signed_id:', signedID)
                .replace(':filename:', fileName),
          })
        })
      })
    }

    fileInput.addEventListener('change', onChange)

    return () => fileInput.removeEventListener('change', onChange)
  }, [fileInput])

  const onClick = (event) => {
    fileInput.click()
  }

  return (
    <Button enabled={enabled} onClick={onClick} {...props}>
      <ImageIcon size={16} />
    </Button>
  )
}

const ToggleBulletListButton = (props) => {
  const { toggleBulletList } = useCommands()

  const enabled = toggleBulletList.enabled()
  const onClick = useCallback(() => {
    toggleBulletList()
  }, [toggleBulletList]);

  return (
    <Button enabled={enabled} onClick={onClick} {...props}>
      <ListIcon size={16} />
    </Button>
  )
}

const ToggleOrderedListButton = (props) => {
  const { toggleOrderedList } = useCommands()

  const enabled = toggleOrderedList.enabled()
  const onClick = useCallback(() => {
    toggleOrderedList()
  }, [toggleOrderedList]);

  return (
    <Button enabled={enabled} onClick={onClick} {...props}>
      <OrderedListIcon size={16} />
    </Button>
  )
}

const isYouTube = (url) => {
  const host = url.host.replace(/^www\./, '');

  return ['youtube.com', 'youtu.be', 'youtube-nocookie.com'].includes(host);
};

const isLoom = (url) => {
  const host = url.host.replace(/^www\./, '');

  return ['loom.com'].includes(host);
};

const EmbedButton = (props) => {
  const { addIframe } = useCommands();
  const chain = useChainedCommands();

  const enabled = addIframe.enabled();
  const onClick = useCallback(() => {
    let src, url;

    const input = prompt('Video URL');

    if (input === '') return;

    try {
      url = new URL(input);
    } catch (error) {
      alert('Invalid URL.');
      return;
    }

    if (isYouTube(url)) {
      if (url.pathname.startsWith('/embed/')) {
        src = url.href;
      } else if (url.pathname.startsWith('/watch')) {
        const v = url.searchParams.get('v');
        src = `https://www.youtube.com/embed/${v}`;
      } else if (url.pathname.startsWith('/shorts/')) {
        url.pathname = url.pathname.replace('shorts', 'embed');
        url.search = '';
        src = url.href;
      } else if (url.host === 'youtu.be') {
        const v = url.pathname.replace(/^\//, '');
        src = `https://www.youtube.com/embed/${v}`;
      }
    } else if (isLoom(url)) {
      if (url.pathname.startsWith('/embed/')) {
        src = url.href;
      } else if (url.pathname.startsWith('/share/')) {
        url.pathname = url.pathname.replace('/share/', '/embed/')
        src = url.href
      }
    }

    if (src) {
      chain
        .focus('start')
        .addIframe({ allowFullScreen: true, frameBorder: 0, src })
        .run();
    } else {
      alert('Unsupported embed URL.');
    }
  }, [addIframe]);

  return (
    <Button enabled={enabled} onClick={onClick} {...props}>
      <EmbedIcon size={16} />
    </Button>
  )
};

const EditorMenu = ({ fileInput, previewPath, progressContainer }) => {
  return (
    <div className="flex flex-row mx-3 my-1 space-x-1 absolute">
      <div role="group">
        <HeadingButton left={true} />
        <BlockquoteButton right={true} />
      </div>

      <div role="group">
        <BoldButton left={true} />
        <ItalicButton right={true} />
      </div>

      <div role="group">
        <ToggleBulletListButton left={true} />
        <ToggleOrderedListButton right={true} />
      </div>

      <div role="group">
        <HorizontalRuleButton left={true} />
        <ImageButton
          fileInput={fileInput}
          previewPath={previewPath}
          progressContainer={progressContainer}
        />
        <EmbedButton right={true} />
      </div>
    </div>
  )
}

export default EditorMenu;
